import React from 'react';
import { Artist } from '../../types/artist';
import './ArtistOverview.css';

interface ArtistOverviewProps {
  artist: Artist;
  showAsModal?: boolean;
  onClose?: () => void;
  showActions?: boolean;
  onEdit?: () => void;
  onView?: () => void;
  className?: string;
}

export const ArtistOverview: React.FC<ArtistOverviewProps> = ({
  artist,
  showAsModal = false,
  onClose,
  showActions = false,
  onEdit,
  onView,
  className = ''
}) => {
  const hasLocation = artist.city || artist.state || artist.country;
  const hasSocialLinks = artist.soundcloudUrl || artist.bandcampUrl || artist.instagramUrl || 
                         artist.youtubeUrl || artist.tikTokUrl || artist.linktreeUrl;

  const formatLocation = () => {
    const parts = [];
    if (artist.city) parts.push(artist.city);
    if (artist.state) parts.push(artist.state);
    if (artist.country) parts.push(artist.country);
    return parts.join(', ');
  };

  const socialLinks = [
    { url: artist.soundcloudUrl, name: 'SoundCloud', icon: '/img/soundcloud.png' },
    { url: artist.bandcampUrl, name: 'Bandcamp', icon: '/img/bandcamp.png' },
    { url: artist.instagramUrl, name: 'Instagram', icon: '/img/instagram.png' },
    { url: artist.youtubeUrl, name: 'YouTube', icon: '/img/youtube.png' },
    { url: artist.tikTokUrl, name: 'TikTok', icon: '/img/tiktok.png' },
    { url: artist.linktreeUrl, name: 'Linktree', icon: '/img/linktree.png' }
  ].filter(link => link.url);

  const handleBackdropClick = (e: React.MouseEvent) => {
    if (e.target === e.currentTarget && onClose) {
      onClose();
    }
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Escape' && onClose) {
      onClose();
    }
  };

  const content = (
    <div className={`artist-overview ${className}`}>
      {/* Header with avatar and basic info */}
      <div className="artist-overview-header">
        {artist.profileImageUrl && (
          <div className="artist-overview-avatar">
            <img 
              src={artist.profileImageUrl} 
              alt={artist.name}
              className="artist-overview-avatar-img"
            />
          </div>
        )}
        <div className="artist-overview-header-info">
          <h2 className="artist-overview-name">{artist.name}</h2>
          {hasLocation && (
            <div className="artist-overview-location">
              <span className="location-icon">📍</span>
              <span>{formatLocation()}</span>
            </div>
          )}
        </div>
        {showAsModal && onClose && (
          <button 
            className="artist-overview-close"
            onClick={onClose}
            aria-label="Close"
          >
            ✕
          </button>
        )}
      </div>

      {/* Bio section */}
      {artist.bio && (
        <div className="artist-overview-bio">
          <p>{artist.bio}</p>
        </div>
      )}

      {/* Social links */}
      {hasSocialLinks && (
        <div className="artist-overview-social">
          <h3 className="social-title">Connect</h3>
          <div className="social-links">
            {socialLinks.map((link, index) => (
              <a
                key={index}
                href={link.url || '#'}
                target="_blank"
                rel="noopener noreferrer"
                className="social-link"
                title={link.name}
              >
                <img src={link.icon} alt={link.name} className="social-icon" />
                <span className="social-name">{link.name}</span>
              </a>
            ))}
          </div>
        </div>
      )}

      {/* Track count */}
      {artist.tracks && artist.tracks.length > 0 && (
        <div className="artist-overview-stats">
          <div className="stat-item">
            <span className="stat-value">{artist.tracks.length}</span>
            <span className="stat-label">{artist.tracks.length === 1 ? 'Track' : 'Tracks'}</span>
          </div>
        </div>
      )}

      {/* Action buttons */}
      {showActions && (
        <div className="artist-overview-actions">
          {onView && (
            <button 
              className="action-button primary"
              onClick={onView}
            >
              View Profile
            </button>
          )}
          {onEdit && (
            <button 
              className="action-button secondary"
              onClick={onEdit}
            >
              Edit
            </button>
          )}
        </div>
      )}
    </div>
  );

  if (showAsModal) {
    return (
      <div 
        className="artist-overview-modal-overlay"
        onClick={handleBackdropClick}
        onKeyDown={handleKeyDown}
        tabIndex={-1}
      >
        <div className="artist-overview-modal-content">
          {content}
        </div>
      </div>
    );
  }

  return content;
};